import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { RouteDataService } from './route-data.service';
import { Point } from '../../../interfaces/custom-route.interface';
import { IndicatorsService } from './indicators.service';
import { MAP_STYLE } from 'src/app/pages/new-custom-route/constants';

@Injectable({ providedIn: 'root' })

export class MapService {
  private _routeDataService: RouteDataService = inject(RouteDataService);
  private _indicatorService: IndicatorsService = inject(IndicatorsService)
  private mapPadding: any = { top: 150, right: 20, bottom: 100, left: 20 };
  public  map: GoogleMap;

  private startPoint = computed<google.maps.LatLngLiteral>(() => this._routeDataService.startPoint());
  private endPoint = computed<google.maps.LatLngLiteral>(() => this._routeDataService.endPoint());
  private currentDayIndex = computed<number>(() => this._routeDataService.currentDayIndex());
  private routeSegments = computed<Point[][]>(() => this._routeDataService.getRouteSegments());
  private reloadMap = computed(() => this._routeDataService.getReloadMap());
  
  public projectedPoint = signal<google.maps.LatLngLiteral>(undefined);
  public projectedPointRotation = signal<number>(undefined);

  private reloadMapEffect = effect(() => {
    if (!this.reloadMap())
      return;

    this.startRoute();
    this._routeDataService.disableReloadMap();
  }, {allowSignalWrites: true})
  
  public initMap(map: GoogleMap): void {
    this.map = map;
  }

  public startRoute(): void {
    this.resetMarkerProjection();
    this.fitBoundsToAllPoints();
    this._indicatorService.resetInstructions();
  }

  public setMarkerAndRotation(itemLocation: google.maps.LatLngLiteral, itemHeading: number): void {
    this.projectedPoint.set(itemLocation);
    this.projectedPointRotation.set(itemHeading);
  }

  public resetMarkerProjection(): void {
    this.projectedPoint.set(null);
    this.projectedPointRotation.set(null);
  }

  public moveMarker(position: google.maps.LatLngLiteral): void {
    // this.map?.googleMap?.setZoom(18);
    // this.map?.googleMap?.panTo(new google.maps.LatLng(position.lat, position.lng));
    this.map.googleMap.setCenter(new google.maps.LatLng(position.lat, position.lng));
  }

  public fitBoundsToAllPoints(): void {
    const routeSegments = untracked(() => this.routeSegments());
    const startPoint = untracked(() => this.startPoint());
    const endPoint = untracked(() => this.endPoint());
    const currentDayIndex = untracked(() => this.currentDayIndex());

    if (!this.map) 
      return;

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(new google.maps.LatLng(startPoint))
    routeSegments?.[currentDayIndex]?.forEach(poly => bounds.extend(new google.maps.LatLng(poly.currentLocation)));
    if (endPoint)
        bounds.extend(new google.maps.LatLng(endPoint))

    this.map.fitBounds(bounds, this.mapPadding);
  }

  public fitCurrentTrameBounds(trameIndex: number): void {
    if (!this.map) 
      return;

    const routeSegments = untracked(() => this.routeSegments());
    const prevLoc = routeSegments?.[this.currentDayIndex()]?.[trameIndex]?.previousLocation?.lat ? routeSegments?.[this.currentDayIndex()]?.[trameIndex]?.previousLocation : this.startPoint();
    const currentTrame = [
      prevLoc,
      routeSegments?.[this.currentDayIndex()]?.[trameIndex]?.currentLocation
    ]

    if (!currentTrame[0] || !currentTrame[1])
      return;
    
    const bounds = new google.maps.LatLngBounds();
    currentTrame?.forEach(coords => bounds.extend(new google.maps.LatLng(coords)));
    
    google.maps.event.addListenerOnce(this.map.googleMap, 'idle', () => {
      this.map.googleMap?.fitBounds(bounds, this.mapPadding);
    });
  }

  public computeProjection(point: google.maps.LatLngLiteral, path: google.maps.LatLngLiteral[]): { projectedPoint: google.maps.LatLngLiteral; segmentIndex: number } | null {
    if (path.length < 2) 
      return null;

    const projection = this.map.getProjection();
    if (!projection) 
      return null;

    const p = projection.fromLatLngToPoint(new google.maps.LatLng(point.lat, point.lng));
    let minDistSquared = Infinity;
    let bestProjectionPoint: google.maps.Point | null = null;
    let bestSegmentIndex = -1;

    for (let i = 0; i < path.length - 1; i++) {
      const v1 = projection.fromLatLngToPoint(new google.maps.LatLng(path[i].lat, path[i].lng));
      const v2 = projection.fromLatLngToPoint(new google.maps.LatLng(path[i + 1].lat, path[i + 1].lng));
      const seg = new google.maps.Point(v2.x - v1.x, v2.y - v1.y);
      const pt = new google.maps.Point(p.x - v1.x, p.y - v1.y);
      const segLen2 = seg.x * seg.x + seg.y * seg.y;
      if (segLen2 === 0) 
        continue;

      let t = (pt.x * seg.x + pt.y * seg.y) / segLen2;
      if (t < 0) 
        t = 0;

      if (t > 1) 
        t = 1;

      const projX = v1.x + t * seg.x;
      const projY = v1.y + t * seg.y;
      const projPoint = new google.maps.Point(projX, projY);
      const dx = p.x - projX;
      const dy = p.y - projY;
      const dist2 = dx * dx + dy * dy;

      if (dist2 < minDistSquared) {
        minDistSquared = dist2;
        bestProjectionPoint = projPoint;
        bestSegmentIndex = i;
      }
    }

    if (bestProjectionPoint === null) 
      return null;

    const projectedLatLng = projection.fromPointToLatLng(bestProjectionPoint).toJSON();
    return {
      projectedPoint: { lat: projectedLatLng.lat, lng: projectedLatLng.lng },
      segmentIndex: bestSegmentIndex
    };
  }

  public setLightMapStyle(): void {
    const styledMap = new google.maps.StyledMapType(MAP_STYLE['light'], { name: 'Light Map' });
    this.map?.googleMap?.mapTypes?.set('map_light', styledMap);
    this.map?.googleMap?.setMapTypeId('map_light');
  }

  public setDarkMapStyle(): void {
    const styledMap = new google.maps.StyledMapType(MAP_STYLE['dark'], { name: 'Dark Map' });
    this.map?.googleMap?.mapTypes?.set('map_dark', styledMap);
    this.map?.googleMap?.setMapTypeId('map_dark');
  }

}